import { APIGetDevices } from '../../../config/restAPI/FetchAPI';
import {
    normalizePolicyOptionId,
    resolveFuelPolicyIdFromPreset,
} from '../vehicleConfigurator/vehiclePolicyConfig';

const DEVICES_PAGE_SIZE = 500;
const PARALLEL_PAGE_BATCH = 4;
const CACHE_TTL_MS = 5 * 60 * 1000;

const attachmentCountsCache = new Map();

const noopSetLoading = () => {};

const isPendingCacheEntry = (cached) => cached && typeof cached.then === 'function';

const getResolvedCacheEntry = (agencyId) => {
    const cached = attachmentCountsCache.get(agencyId);
    if (!cached || isPendingCacheEntry(cached)) {
        return null;
    }
    if (Date.now() - cached.fetchedAt >= CACHE_TTL_MS) {
        return null;
    }
    return cached;
};

/** Fuel policy OID bound on a device via embedded vehicle preset hardware policy. */
export function resolveFuelPolicyIdFromDevice(device = {}) {
    const preset = device.vehiclePreset || device.vehicle_preset;
    if (!preset) {
        return '';
    }
    return resolveFuelPolicyIdFromPreset(preset);
}

/** Count devices per fuel policy OID from vehiclePreset.hardwarePolicy bindings. */
export function buildFuelPolicyAttachmentCountsFromDevices(devices = []) {
    const counts = {};
    (Array.isArray(devices) ? devices : []).forEach((device) => {
        const policyId = resolveFuelPolicyIdFromDevice(device);
        if (!policyId) {
            return;
        }
        const key = normalizePolicyOptionId(policyId);
        counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
}

function mergeAttachmentCounts(target, source) {
    const merged = { ...target };
    Object.entries(source).forEach(([key, value]) => {
        merged[key] = (merged[key] || 0) + value;
    });
    return merged;
}

export function getFuelPolicyAssetsAttachedCount(attachmentCounts, policyId) {
    if (!attachmentCounts) {
        return null;
    }
    const key = normalizePolicyOptionId(policyId);
    if (!key) {
        return 0;
    }
    return attachmentCounts[key] ?? 0;
}

/** API-provided attachment count on a fuel policy document. */
export function resolveFuelPolicyAssetsAttachedFromApi(doc = {}) {
    if (typeof doc.assetsAttached === 'number') {
        return doc.assetsAttached;
    }
    const associated = doc.associatedAsset || doc.associatedAssets;
    if (Array.isArray(associated)) {
        return associated.length;
    }
    return 0;
}

export function resolveFuelPolicyAssetsAttached(doc, attachmentCounts) {
    const deviceCount = getFuelPolicyAssetsAttachedCount(attachmentCounts, doc?.oid || doc?.id);
    if (deviceCount != null) {
        return deviceCount;
    }
    return resolveFuelPolicyAssetsAttachedFromApi(doc);
}

export function invalidateFuelPolicyAttachmentCountsCache(agencyId) {
    if (agencyId) {
        attachmentCountsCache.delete(agencyId);
    } else {
        attachmentCountsCache.clear();
    }
}

async function fetchDevicePage(token, agencyId, pageNumber) {
    const result = await APIGetDevices(noopSetLoading, token, agencyId, {
        pageNumber,
        totalPerPage: DEVICES_PAGE_SIZE,
    });

    if (result?.data?.status !== 200) {
        return null;
    }

    const { data, totalData: total } = result.data;
    if (!Array.isArray(data)) {
        return null;
    }

    return { data, totalData: total };
}

async function fetchDevicePageBatch(token, agencyId, pageNumbers) {
    const pages = await Promise.all(
        pageNumbers.map((pageNumber) => fetchDevicePage(token, agencyId, pageNumber)),
    );

    pages.forEach((page, index) => {
        if (page == null) {
            throw new Error(`Failed to fetch devices page ${pageNumbers[index]}`);
        }
    });

    return pages.reduce((counts, page) => {
        if (!page.data.length) {
            return counts;
        }
        return mergeAttachmentCounts(
            counts,
            buildFuelPolicyAttachmentCountsFromDevices(page.data),
        );
    }, {});
}

async function fetchDevicePagesInParallel(token, agencyId, pageNumbers) {
    const batches = [];
    for (let i = 0; i < pageNumbers.length; i += PARALLEL_PAGE_BATCH) {
        batches.push(pageNumbers.slice(i, i + PARALLEL_PAGE_BATCH));
    }

    return batches.reduce(
        async (countsPromise, batch) => {
            const counts = await countsPromise;
            const batchCounts = await fetchDevicePageBatch(token, agencyId, batch);
            return mergeAttachmentCounts(counts, batchCounts);
        },
        Promise.resolve({}),
    );
}

/**
 * Build policyId → device count map from device hardware-policy bindings.
 * Uses a short-lived cache and parallel page fetches; counts incrementally per page.
 */
export async function fetchFuelPolicyAttachmentCounts(token, agencyId, { forceRefresh = false } = {}) {
    if (!token || !agencyId) {
        return null;
    }

    if (forceRefresh) {
        attachmentCountsCache.delete(agencyId);
    } else {
        const cached = attachmentCountsCache.get(agencyId);
        if (cached) {
            if (isPendingCacheEntry(cached)) {
                return cached;
            }
            const resolved = getResolvedCacheEntry(agencyId);
            if (resolved) {
                return resolved.counts;
            }
        }
    }

    const promise = (async () => {
        try {
            const firstPage = await fetchDevicePage(token, agencyId, 1);
            if (!firstPage) {
                attachmentCountsCache.delete(agencyId);
                return null;
            }

            let counts = buildFuelPolicyAttachmentCountsFromDevices(firstPage.data);
            const total = typeof firstPage.totalData === 'number'
                ? firstPage.totalData
                : firstPage.data.length;
            const totalPages = Math.max(1, Math.ceil(total / DEVICES_PAGE_SIZE));

            if (totalPages > 1) {
                const remainingPages = Array.from(
                    { length: totalPages - 1 },
                    (_, index) => index + 2,
                );
                const pageCounts = await fetchDevicePagesInParallel(token, agencyId, remainingPages);
                counts = mergeAttachmentCounts(counts, pageCounts);
            }

            attachmentCountsCache.set(agencyId, { counts, fetchedAt: Date.now() });
            return counts;
        } catch {
            attachmentCountsCache.delete(agencyId);
            return null;
        }
    })();

    attachmentCountsCache.set(agencyId, promise);
    return promise;
}

/** Read a single policy's attachment count from cache, or null if cache is cold or in flight. */
export function getCachedFuelPolicyAssetsAttachedCount(agencyId, policyId) {
    const resolved = getResolvedCacheEntry(agencyId);
    if (!resolved) {
        return null;
    }
    return getFuelPolicyAssetsAttachedCount(resolved.counts, policyId);
}
